// MarkPaidDialog — how a commission worksheet gets stamped paid.
//
// Replaces two free-text prompt() boxes. The method is a picker over the five
// values the database accepts (shared/comp/paid-method.ts), so a typo can no
// longer reach the CHECK constraint. A refusal from the server is shown in
// the dialog and the dialog stays open: it is never swallowed (Sentry
// JAVASCRIPT-REACT-5M, 2026-10-30).

import { useEffect, useState } from "react";

import {
  PAID_METHODS,
  PAID_METHOD_LABELS,
  defaultPaidMethod,
  type PaidMethod,
} from "../../../shared/comp/paid-method";
import { Button, Modal } from "../ui";

export interface MarkPaidDialogProps {
  open: boolean;
  /** Payroll is preselected when the advisor is paid through Gusto. */
  paidThroughGusto: boolean;
  onClose: () => void;
  /** Resolve on success; throw to show the message in the dialog. */
  onSubmit: (method: PaidMethod, reference: string) => Promise<void>;
}

const fieldStyle = {
  width: "100%",
  minHeight: 44,
  padding: "8px 12px",
  borderRadius: 6,
  border: "1px solid var(--mid-gray)",
  fontSize: 15,
  boxSizing: "border-box",
} as const;

export function MarkPaidDialog({
  open,
  paidThroughGusto,
  onClose,
  onSubmit,
}: MarkPaidDialogProps) {
  const [method, setMethod] = useState<PaidMethod>(defaultPaidMethod(paidThroughGusto));
  const [reference, setReference] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Start fresh each time it opens.
  useEffect(() => {
    if (!open) return;
    setMethod(defaultPaidMethod(paidThroughGusto));
    setReference("");
    setError(null);
  }, [open, paidThroughGusto]);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSubmit(method, reference.trim());
    } catch (e) {
      setError(
        e instanceof Error && e.message
          ? e.message
          : "Couldn't mark this worksheet paid. Nothing was saved. Try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={saving ? () => undefined : onClose}
      title="Mark paid"
      sub="Record how this was paid. It can't be undone from here."
      size="sm"
      footer={
        <>
          <Button variant="ghost" className="tt-floor" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button className="tt-floor" onClick={() => void submit()} disabled={saving}>
            {saving ? "Saving…" : "Mark paid"}
          </Button>
        </>
      }
    >
      {error && (
        <div
          role="alert"
          style={{
            padding: "10px 14px",
            marginBottom: 14,
            borderRadius: 10,
            border: "1px solid var(--danger)",
            color: "var(--danger)",
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}
      <label htmlFor="mark-paid-method" style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>
        Payment method
      </label>
      <select
        id="mark-paid-method"
        value={method}
        onChange={(e) => setMethod(e.target.value as PaidMethod)}
        style={fieldStyle}
      >
        {PAID_METHODS.map((m) => (
          <option key={m} value={m}>
            {PAID_METHOD_LABELS[m]}
          </option>
        ))}
      </select>
      <label
        htmlFor="mark-paid-reference"
        style={{ display: "block", fontWeight: 600, margin: "14px 0 6px" }}
      >
        Payment reference (optional)
      </label>
      <input
        id="mark-paid-reference"
        type="text"
        value={reference}
        onChange={(e) => setReference(e.target.value)}
        placeholder="Check number or wire id"
        style={fieldStyle}
      />
    </Modal>
  );
}
